import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { AppState } from '@app/app-state/models/app-state';
import { faSave } from '@fortawesome/free-regular-svg-icons';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Account, TransferAccount } from '@models/account.model';
import { ApplicationTypeNames, FormControlState } from '@models/application-type.model';
import { License } from '@models/license.model';
import { Store } from '@ngrx/store';
import { EstablishmentWatchWordsService } from '@services/establishment-watch-words.service';
import { FeatureFlagService } from '@services/feature-flag.service';
import { LicenseDataService } from '@services/license-data.service';
import { ApplicationHTMLContent, FormBase } from '@shared/form-base';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, filter, mergeMap, takeWhile } from 'rxjs/operators';

const ValidationErrorMap = {
  transferConsent: 'Please consent to terminate the third party operator relationship'
};

@Component({
  selector: 'app-terminate-tpo-relationship',
  templateUrl: './terminate-tpo-relationship.component.html',
  styleUrls: ['./terminate-tpo-relationship.component.scss']
})
export class TerminateTPORelationshipComponent extends FormBase implements OnInit {
  faSave = faSave;
  faTrashAlt = faTrashAlt;
  licence: License;
  form: FormGroup;
  licenceId: string;
  busy: Subscription;
  validationMessages: any[];
  showValidationMessages: boolean;
  htmlContent = {} as ApplicationHTMLContent;
  ApplicationTypeNames = ApplicationTypeNames;
  FormControlState = FormControlState;
  account: Account;
  minDate = new Date();

  constructor(
    private store: Store<AppState>,
    public snackBar: MatSnackBar,
    public router: Router,
    private licenseDataService: LicenseDataService,
    public featureFlagService: FeatureFlagService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    public dialog: MatDialog,
    public establishmentWatchWordsService: EstablishmentWatchWordsService
  ) {
    super();
    this.route.paramMap.subscribe((pmap) => (this.licenceId = pmap.get('licenceId')));
  }

  ngOnInit() {
    this.form = this.fb.group({
      establishmentName: [''],
      establishmentAddressStreet: [''],
      establishmentAddressCity: [''],
      establishmentAddressPostalCode: [''],
      establishmentParcelId: [''],
      transferConsent: ['', [this.customRequiredCheckboxValidator()]]
    });

    this.store
      .select((state) => state.currentAccountState.currentAccount)
      .pipe(takeWhile(() => this.componentActive))
      .pipe(filter((account) => !!account))
      .subscribe((account) => {
        this.account = account;
      });

    this.busy = this.licenseDataService
      .getLicenceById(this.licenceId)
      .pipe(takeWhile(() => this.componentActive))
      .subscribe(
        (data: License) => {
          this.licence = data;
          this.form.patchValue(data);
        },
        () => {
          console.log('Error occured');
        }
      );
  }

  /**
   * Save form data
   * @param showProgress
   */
  save(showProgress: boolean = false): Observable<boolean> {
    return this.licenseDataService
      .terminateThirdPartyOperator(this.licence.id, this.account.id)
      .pipe(takeWhile(() => this.componentActive))
      .pipe(
        catchError(() => {
          this.snackBar.open('Error submitting terminate relationship', 'Fail', {
            duration: 3500,
            panelClass: ['red-snackbar']
          });
          return of(false);
        })
      )
      .pipe(
        mergeMap(() => {
          if (showProgress === true) {
            this.snackBar.open('Third Party Operator relationship terminated', 'Success', {
              duration: 2500,
              panelClass: ['green-snackbar']
            });
          }
          return of(true);
        })
      );
  }

  /**
   * Terminate third party operator relationship
   * */
  terminateTPORelationship() {
    if (!this.isValid()) {
      this.showValidationMessages = true;
    } else {
      this.busy = this.save(true)
        .pipe(takeWhile(() => this.componentActive))
        .subscribe((result: boolean) => {
          if (result) {
            this.router.navigate(['/licences']);
          }
        });
    }
  }

  isValid(): boolean {
    this.markControlsAsTouched(this.form);
    this.showValidationMessages = false;
    this.validationMessages = this.listControlsWithErrors(this.form, ValidationErrorMap);

    return this.form.valid;
  }

  businessTypeIsPartnership(): boolean {
    return (
      this.account &&
      ['GeneralPartnership', 'LimitedPartnership', 'LimitedLiabilityPartnership', 'Partnership'].indexOf(
        this.account.businessType
      ) !== -1
    );
  }

  businessTypeIsPrivateCorporation(): boolean {
    return (
      this.account &&
      ['PrivateCorporation', 'UnlimitedLiabilityCorporation', 'LimitedLiabilityCorporation'].indexOf(
        this.account.businessType
      ) !== -1
    );
  }

  showFormControl(state: string): boolean {
    return [FormControlState.Show.toString(), FormControlState.ReadOnly.toString()].indexOf(state) !== -1;
  }

  onAccountSelect(proposedAccount: TransferAccount) {
    this.form.get('proposedOwner').patchValue(proposedAccount);
  }
}
